import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme.js';
import { useAuth } from '../context/AuthContext.js';
import { useMe, type MyOrg } from '../lib/queries.js';
import { Icon } from './Icon.js';
import { Logo } from './Logo.js';

const ROLE_LABEL: Record<string, string> = { admin: 'Admin', team_member: 'Member' };

/** The units a person can use, one row each; the one in use is ticked. */
function UnitList({ orgs, current, onPick }: { orgs: MyOrg[]; current: string | null; onPick: (id: string) => void }) {
  return (
    <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 14, overflow: 'hidden' }}>
      {orgs.map((o, i) => (
        <button
          key={o.id}
          onClick={() => onPick(o.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 16px',
            background: 'none', border: 'none', borderTop: i ? `1px solid ${theme.border}` : 'none',
            textAlign: 'left', cursor: 'pointer', fontFamily: theme.font,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>{o.name}</div>
            <div style={{ fontSize: 12, color: theme.textMuted }}>{ROLE_LABEL[o.role] ?? o.role}</div>
          </div>
          {o.id === current && <span style={{ color: theme.accent }}><Icon name="check" size={18} /></span>}
        </button>
      ))}
    </div>
  );
}

/** Full screen, for someone in several units who hasn't picked one yet. */
export function UnitPickerPage() {
  const { data: me } = useMe();
  const { switchOrg } = useAuth();
  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', background: theme.bg, padding: 24, fontFamily: theme.font,
    }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 10 }}><Logo size={24} /></div>
        <div style={{ fontSize: 14, color: theme.textMuted, textAlign: 'center', marginBottom: 20 }}>
          Which unit are you using? You can switch any time from Home.
        </div>
        <UnitList orgs={me?.orgs ?? []} current={null} onPick={switchOrg} />
      </div>
    </div>
  );
}

/**
 * Home's title: the unit in use. For someone in more than one unit it opens
 * a list to switch; everyone else sees the app name with their unit under it.
 */
export function UnitSwitcher() {
  const nav = useNavigate();
  const { data: me } = useMe();
  const { orgId, switchOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const orgs = me?.orgs ?? [];
  const titleStyle = { fontSize: 22, fontWeight: 700, color: theme.text, letterSpacing: -0.5, lineHeight: 1.15 };

  if (orgs.length < 2) {
    return (
      <div>
        <Logo size={22} />
        {orgs[0] && <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 2 }}>{orgs[0].name}</div>}
      </div>
    );
  }

  const current = orgs.find((o) => o.id === orgId);
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Switch unit"
        style={{
          ...titleStyle, display: 'flex', alignItems: 'center', gap: 4, maxWidth: '100%',
          background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: theme.font,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current?.name ?? 'MusterTap'}</span>
        <Icon name="chevronD" size={18} />
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 21,
            width: 'min(300px, calc(100vw - 36px))', boxShadow: '0 8px 24px rgba(0,0,0,0.14)', borderRadius: 14,
          }}>
            <UnitList
              orgs={orgs}
              current={orgId}
              onPick={(id) => { setOpen(false); switchOrg(id); nav('/', { replace: true }); }}
            />
          </div>
        </>
      )}
    </div>
  );
}
